// src/hooks/useUserRole.ts
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { useSession } from "./useSession";

export interface UserRoleData {
  clerk_user_id: string;
  role: "super_admin" | "admin" | "user";
}

const userRoleQueryKey = ["admin", "me", "role"] as const;

type UseUserRoleOptions = Pick<
  UseQueryOptions<UserRoleData, Error, UserRoleData, typeof userRoleQueryKey>,
  "enabled" | "staleTime" | "retry"
>;

export function useUserRole(options?: UseUserRoleOptions) {
  const { isReady, isAuthenticated } = useSession();
  const isAuthEnabled = isReady && isAuthenticated;

  const query = useQuery<UserRoleData, Error, UserRoleData, typeof userRoleQueryKey>({
    queryKey: userRoleQueryKey,
    queryFn: async () => {
      const res = await apiService.getMyRole();
      return res.data;
    },
    enabled: isAuthEnabled && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 60_000,
    retry: options?.retry ?? 1,
  });

  return {
    role: query.data?.role ?? "user",
    isSuper: query.data?.role === "super_admin",
    isAdmin: query.data?.role === "admin" || query.data?.role === "super_admin",
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
